/**
 * TaskShortcutsRow — the 4-button status-message action row shared
 * between Planning Board's Project cards and Team cards.
 *
 * Renders:
 *   1. Email     — mailto: with formatted subject + task-list body
 *   2. WhatsApp  — copies message + opens wa.me deep-link
 *   3. Copy text — writes the plain-text task list to the clipboard
 *   4. Copy image — snapshots the *entire* nearest card (via
 *      `data-shortcut-container="<containerId>"`) and writes the PNG
 *      to the clipboard.
 *
 * Every button is a no-op stub when the message would be empty
 * (recipient missing + task list empty) so the row keeps a stable
 * height without misfiring in an empty state.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, Copy, Image as ImageIcon } from "lucide-react";

const fmtDue = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const now = new Date();
  const diffDays = Math.round((dt - now) / 86400000);
  const abs = dt.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  if (diffDays < 0) return `${abs} · ${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return `${abs} · today`;
  if (diffDays <= 7) return `${abs} · in ${diffDays}d`;
  return abs;
};

/** Interpolate {placeholders} in a template string. */
function interpolate(tpl, vars) {
  if (!tpl) return "";
  return String(tpl).replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : ""));
}

/** Turn a task list into the numbered lines used in `{tasks}`. */
function renderTaskLines({ tasks, projectName, projectsById }) {
  if (!tasks?.length) return "(No tasks in this view.)";
  return tasks.map((t, i) => {
    const proj = projectsById?.get?.(t.project_id);
    const due = t.due_date ? ` · due ${fmtDue(t.due_date)}` : "";
    const status = t.status ? ` [${t.status}]` : "";
    const priority = t.priority && t.priority !== "medium" ? ` (${t.priority})` : "";
    return `${i + 1}. ${t.title}${status}${priority}${due}${proj && !projectName ? ` · ${proj.name}` : ""}`;
  }).join("\n");
}

/** Default template bundle — kept in sync with StatusTemplatesDialog. */
const DEFAULT_TPL = {
  email_subject: "Status check · {count} task{plural} · {workspace}",
  email_body:
    "Hi {greeting},\n\n" +
    "Quick status check on the tasks you're currently owning in {workspace}:\n\n" +
    "{tasks}\n\n" +
    "Could you share a short status update on each?\n\n" +
    "Thanks!\n{sender}",
  whatsapp_body:
    "Hi {greeting}, quick nudge from {workspace}:\n\n{tasks}\n\n" +
    "Please share a short status on each. Thanks!",
  text_body: "Hi {greeting},\n\nCurrent tasks:\n{tasks}\n\nCheers,\n{sender}",
};

function makeVars({ recipient, ownerName, tasks, workspaceName, projectName, projectsById, senderName }) {
  const greeting = (recipient?.name || ownerName || "").split(" ")[0] || "there";
  return {
    name: recipient?.name || ownerName || projectName || "",
    greeting,
    workspace: workspaceName || "",
    count: String(tasks?.length || 0),
    plural: (tasks?.length || 0) === 1 ? "" : "s",
    tasks: renderTaskLines({ tasks, projectName, projectsById }),
    sender: senderName || "",
    project: projectName || "",
  };
}

/** Build the shared body used by Email/WhatsApp/Copy-text. Falls back to
 *  the legacy hard-coded copy when the template is empty. */
export function buildStatusMessage({ templates, recipient, ownerName, tasks = [], workspaceName = "", projectName = "", projectsById = new Map(), senderName = "" }) {
  const t = { ...DEFAULT_TPL, ...(templates || {}) };
  const vars = makeVars({ recipient, ownerName, tasks, workspaceName, projectName, projectsById, senderName });
  return interpolate(t.email_body, vars);
}

export function buildWhatsAppMessage({ templates, recipient, ownerName, tasks = [], workspaceName = "", projectName = "", projectsById = new Map(), senderName = "" }) {
  const t = { ...DEFAULT_TPL, ...(templates || {}) };
  const vars = makeVars({ recipient, ownerName, tasks, workspaceName, projectName, projectsById, senderName });
  return interpolate(t.whatsapp_body, vars);
}

export function buildCopyText({ templates, recipient, ownerName, tasks = [], workspaceName = "", projectName = "", projectsById = new Map(), senderName = "" }) {
  const t = { ...DEFAULT_TPL, ...(templates || {}) };
  const vars = makeVars({ recipient, ownerName, tasks, workspaceName, projectName, projectsById, senderName });
  return interpolate(t.text_body, vars);
}

export function buildStatusSubject({ templates, tasks = [], workspaceName = "", projectName = "", recipient = null, ownerName = "" }) {
  const t = { ...DEFAULT_TPL, ...(templates || {}) };
  const vars = makeVars({ recipient, ownerName, tasks, workspaceName, projectName });
  return interpolate(t.email_subject, vars);
}

/**
 * @param recipient  {name, email, phone} — used by Email + WhatsApp buttons.
 * @param tasks      Array of tasks (title, status, priority, due_date, project_id).
 * @param containerId The `data-shortcut-container` attribute value of the card
 *                    element you want the Copy-image button to snapshot.
 * @param projectName / workspaceName influence the message wording.
 */
export default function TaskShortcutsRow({
  templates = null,
  recipient = null,
  ownerName = "",
  senderName = "",
  tasks = [],
  workspaceName = "",
  projectName = "",
  projectsById,
  containerId,
  compact = false,
  className = "",
}) {
  const [busy, setBusy] = useState(null);

  const commonArgs = { templates, recipient, ownerName, senderName, tasks, workspaceName, projectName, projectsById };
  const emailBody = () => buildStatusMessage(commonArgs);
  const waBody = () => buildWhatsAppMessage(commonArgs);
  const copyBody = () => buildCopyText(commonArgs);
  const subject = buildStatusSubject(commonArgs);

  const doEmail = () => {
    if (!recipient?.email) { toast.error("No email on file for this recipient"); return; }
    const body = encodeURIComponent(emailBody());
    const subj = encodeURIComponent(subject);
    window.location.href = `mailto:${recipient.email}?subject=${subj}&body=${body}`;
  };

  const doWhatsApp = async () => {
    const msg = waBody();
    try { await navigator.clipboard.writeText(msg); } catch { /* ignore */ }
    const phone = (recipient?.phone || "").replace(/\D/g, "");
    const encoded = encodeURIComponent(msg);
    const url = phone ? `https://wa.me/${phone}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Message copied · opening WhatsApp");
  };

  const doCopyText = async () => {
    try {
      await navigator.clipboard.writeText(copyBody());
      toast.success("Task list copied to clipboard");
    } catch { toast.error("Clipboard blocked by browser"); }
  };

  const doCopyImage = async () => {
    if (!containerId) { toast.error("Snapshot target missing"); return; }
    setBusy("image");
    try {
      const node = document.querySelector(`[data-shortcut-container="${containerId}"]`);
      if (!node) throw new Error("Card not found");
      const { toBlob } = await import("html-to-image");
      const blob = await toBlob(node, {
        pixelRatio: 2, backgroundColor: "#ffffff", cacheBust: true,
      });
      if (!blob) throw new Error("Snapshot returned empty");
      if (navigator.clipboard && typeof ClipboardItem !== "undefined") {
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
        toast.success("Card image copied to clipboard");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${projectName || recipient?.name || "card"}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Card downloaded as PNG");
      }
    } catch (e) {
      toast.error(`Snapshot failed: ${e.message || e}`);
    } finally { setBusy(null); }
  };

  const btn = compact
    ? "inline-flex items-center gap-0.5 text-[10px] px-1 py-0.5 border border-border hover:border-primary hover:bg-primary/5 text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed rounded-none"
    : "inline-flex items-center gap-1 text-[10.5px] px-1.5 py-1 border border-border hover:border-primary hover:bg-primary/5 text-muted-foreground hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed rounded-none";

  return (
    <div className={`flex items-center gap-1 flex-wrap ${className}`}
         data-testid={`task-shortcuts-row-${containerId || "card"}`}>
      <button type="button" onClick={doEmail}
              disabled={!recipient?.email}
              className={btn}
              title={recipient?.email ? `Email ${recipient.email} for status` : "No email on file"}
              data-testid={`task-shortcut-email-${containerId || "card"}`}>
        <Mail className="w-3.5 h-3.5" /> {compact ? "" : "Email"}
      </button>
      <button type="button" onClick={doWhatsApp}
              className={btn}
              title={recipient?.phone ? `WhatsApp ${recipient.phone}` : "Open WhatsApp (no phone on file)"}
              data-testid={`task-shortcut-whatsapp-${containerId || "card"}`}>
        <MessageCircle className="w-3.5 h-3.5" /> {compact ? "" : "WhatsApp"}
      </button>
      <button type="button" onClick={doCopyText}
              className={btn}
              title="Copy task list as text"
              data-testid={`task-shortcut-copy-text-${containerId || "card"}`}>
        <Copy className="w-3.5 h-3.5" /> {compact ? "" : "Copy text"}
      </button>
      <button type="button" onClick={doCopyImage}
              disabled={busy === "image"}
              className={btn}
              title="Copy card snapshot as image"
              data-testid={`task-shortcut-copy-image-${containerId || "card"}`}>
        <ImageIcon className="w-3.5 h-3.5" /> {compact ? "" : (busy === "image" ? "Copying…" : "Copy image")}
      </button>
    </div>
  );
}
